import { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from 'src/lib/utils';
import { portalDs } from 'src/components/portal-design-system';

interface IoBoldsignSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
  className?: string;
}

export function IoBoldsignSearch({
  value,
  onChange,
  placeholder = 'Search insertion orders...',
  debounceMs = 500,
  className,
}: IoBoldsignSearchProps) {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (localValue !== value) {
        onChange(localValue);
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [localValue, onChange, value, debounceMs]);

  return (
    <div className={cn(portalDs.searchBar, 'w-full max-w-md', className)}>
      <Search className={portalDs.searchIcon} />
      <input
        type="text"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        placeholder={placeholder}
        className={portalDs.searchInput}
      />
      {localValue ? (
        <button
          type="button"
          onClick={() => {
            setLocalValue('');
            onChange('');
          }}
          className="text-[#9CA3AF] transition-colors hover:text-[#5E6278]"
          aria-label="Clear search"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}

interface IoBoldsignAutocompleteProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  suggestions?: string[];
  required?: boolean;
  disabled?: boolean;
  type?: string;
  hasError?: boolean;
}

export function IoBoldsignAutocomplete({
  id,
  value,
  onChange,
  placeholder,
  suggestions = [],
  required,
  disabled,
  type = 'text',
  hasError = false,
}: IoBoldsignAutocompleteProps) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!value || !suggestions.length) {
      setFilteredSuggestions([]);
      return;
    }

    const filtered = suggestions
      .filter((suggestion) =>
        suggestion &&
        suggestion.toLowerCase().includes(value.toLowerCase()) &&
        suggestion.toLowerCase() !== value.toLowerCase()
      )
      .slice(0, 5);

    setFilteredSuggestions(filtered);
  }, [value, suggestions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative">
      <input
        ref={inputRef}
        id={id}
        type={type}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setShowSuggestions(e.target.value.length > 0);
        }}
        onFocus={() => {
          if (value.length > 0 && filteredSuggestions.length > 0) {
            setShowSuggestions(true);
          }
        }}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className={cn(portalDs.input, hasError && portalDs.inputError)}
      />
      {showSuggestions && filteredSuggestions.length > 0 ? (
        <div
          ref={suggestionsRef}
          className="absolute z-50 mt-1 max-h-40 w-full overflow-y-auto rounded-lg border border-[#EDEEF2] bg-white shadow-[0_2px_8px_rgba(13,15,26,0.08)]"
        >
          {filteredSuggestions.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              className="w-full border-b border-[#EDEEF2] px-3 py-2 text-left text-[13.5px] text-[#0D0F1A] last:border-b-0 hover:bg-[#F0F1F5]"
              onClick={() => {
                onChange(suggestion);
                setShowSuggestions(false);
                inputRef.current?.focus();
              }}
            >
              {suggestion}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
